import { GridNodeTopology, GridEdgeTopology, TabType, AppState, AppAction, GridTopology } from './types';
import { getScenario } from './scenarios/networks';
import { defaultLoadCurve, defaultWindCurve, defaultSolarCurve } from './scenarios/loadCurves';
import { gridStateToTopology } from './utils/gridUtils';
import { UndoStore } from './utils/UndoStore';
import {
  RuleSource,
  RuleSetSource,
  defaultRuleSetSource,
  finalSocFor,
  FINAL_SOC_RULE,
  NEW_RULE_NAME,
  NEW_COST_NAME,
  newRuleSource,
  newCostSource,
  uniqueRuleName,
} from './powerFlow/language/ruleSet';
import { ruleToString } from './powerFlow/language/ruleAst';
import { parseRule, parseCostTerm } from './powerFlow/language/parse';
import { defaultAcRuleSetSource } from './powerFlow/nonlinear/acPowerFlow';

/** The rule set the editing actions target: the ACPF one in AC mode, else DCOPF's. */
const rulesKey = (state: AppState): 'acRules' | 'rules' =>
  state.mode === 'acpf' ? 'acRules' : 'rules';

export const initialState: AppState = {
  gridTopology: null,
  history: new UndoStore<GridTopology | null>(null),
  documentName: '',
  loadCurve: {
    controlPoints: defaultLoadCurve,
  },
  windCurve: {
    controlPoints: defaultWindCurve,
  },
  solarCurve: {
    controlPoints: defaultSolarCurve,
  },
  hoverState: {
    hoverHour: null,
    element: null,
  },
  selectedElement: null,
  isLoading: true,
  initialLoadComplete: false,
  nodeCounter: 15,
  edgeCounter: 21,
  showImportExport: false,
  importExportText: '',
  activeTab: 'gen',
  revealSeq: 0,
  currentScenario: 'simple',
  mode: 'dcopf',
  numTimeSteps: 24,
  requireMatchingSOC: false,
  rules: defaultRuleSetSource(false),
  acRules: defaultAcRuleSetSource(),
};

/** Replace one entry (matched by id) in a named list — the rules or the costs. */
function mapEntry(
  entries: RuleSource[],
  id: string,
  update: (entry: RuleSource) => RuleSource,
): RuleSource[] {
  return entries.map((entry) => (entry.id === id ? update(entry) : entry));
}

/** Replace one rule (matched by id) in a rule set. */
function mapRule(
  rules: RuleSetSource,
  id: string,
  update: (rule: RuleSource) => RuleSource,
): RuleSetSource {
  return { ...rules, rules: mapEntry(rules.rules, id, update) };
}

/** Replace one cost category (matched by id) in a rule set. */
function mapCost(
  rules: RuleSetSource,
  id: string,
  update: (cost: RuleSource) => RuleSource,
): RuleSetSource {
  return { ...rules, costs: mapEntry(rules.costs, id, update) };
}

/**
 * Take an entry's new text, and its new name with it. The name is part of the
 * text now, so an edit can rename the entry; the cached `name` follows whenever
 * the text parses and holds its last value when it doesn't, so a rule keeps its
 * label and its place in the tree through the keystrokes that don't.
 */
function renamed(
  entry: RuleSource,
  source: string,
  parse: (text: string) => { name: string } = parseRule,
): RuleSource {
  try {
    return { ...entry, source, name: parse(source).name };
  } catch {
    return { ...entry, source };
  }
}

/**
 * Apply a discrete grid-topology edit and record it as a single undoable step.
 * Use this for atomic actions (add/remove a node or edge, change a dropdown).
 */
function commitTopology(state: AppState, topology: GridTopology): AppState {
  return {
    ...state,
    gridTopology: topology,
    history: state.history.record(topology),
  };
}

/**
 * Apply a transient grid-topology edit *without* recording history. Used for
 * continuous gestures (dragging a node, dragging a slider, typing into a
 * field) so an entire gesture becomes one undo step. The live value is
 * committed to history later by COMMIT_TOPOLOGY, dispatched when the gesture
 * ends (drag mouse-up or input blur).
 */
function editTopology(state: AppState, topology: GridTopology): AppState {
  return { ...state, gridTopology: topology };
}

/**
 * Load a fresh document, replacing the topology and starting a new undo
 * history (undo does not cross document boundaries).
 */
function loadDocument(
  state: AppState,
  topology: GridTopology | null,
  documentName: string,
): AppState {
  return {
    ...state,
    gridTopology: topology,
    history: new UndoStore<GridTopology | null>(topology),
    documentName,
  };
}

export function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_INITIAL_LOAD_COMPLETE':
      return { ...state, initialLoadComplete: action.payload };
    
    case 'SET_GRID_TOPOLOGY': {
      return {
        ...state,
        gridTopology: action.payload,
        history: new UndoStore<GridTopology | null>(action.payload),
      };
    }
    
    case 'SET_NODE_COUNTER':
      return { ...state, nodeCounter: action.payload };
    
    case 'SET_EDGE_COUNTER':
      return { ...state, edgeCounter: action.payload };
    
    case 'SET_SHOW_IMPORT_EXPORT':
      return { ...state, showImportExport: action.payload };
    
    case 'SET_IMPORT_EXPORT_TEXT':
      return { ...state, importExportText: action.payload };
    
    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload };
    
    case 'SET_CURRENT_SCENARIO':
      return { ...state, currentScenario: action.payload };
    
    case 'LOAD_SCENARIO': {
      const { scenario, gridTopology: urlGridTopology } = action.payload;
      const scenarioName = getScenario(scenario)?.name ?? scenario;
      let newState = { ...state, currentScenario: scenario, documentName: scenarioName };

      if (urlGridTopology) {
        // Use the provided topology from URL
        newState.gridTopology = urlGridTopology;

        // Update counters based on topology
        const maxNodeNum = Math.max(...urlGridTopology.nodes.map((n: GridNodeTopology) => {
          const match = n.id.match(/\d+$/);
          return match ? parseInt(match[0]) : 0;
        }), 0);
        const maxEdgeNum = Math.max(...urlGridTopology.edges.map((e: GridEdgeTopology) => {
          const match = e.id.match(/\d+$/);
          return match ? parseInt(match[0]) : 0;
        }), 0);
        
        newState.nodeCounter = maxNodeNum + 1;
        newState.edgeCounter = maxEdgeNum + 1;
      } else {
        const scenarioData = getScenario(scenario);
        if (scenarioData) {
          // Convert scenario GridState to GridTopology
          const topology = gridStateToTopology(scenarioData.gridState);
          newState.gridTopology = topology;
          console.log(`Loaded ${scenario} scenario`);
          
          // Update counters based on scenario data
          const maxNodeNum = Math.max(...scenarioData.gridState.nodes.map(n => {
            const match = n.id.match(/\d+$/);
            return match ? parseInt(match[0]) : 0;
          }), 0);
          const maxEdgeNum = Math.max(...scenarioData.gridState.edges.map(e => {
            const match = e.id.match(/\d+$/);
            return match ? parseInt(match[0]) : 0;
          }), 0);
          
          newState.nodeCounter = maxNodeNum + 1;
          newState.edgeCounter = maxEdgeNum + 1;
        }
      }
      
      return {
        ...newState,
        history: new UndoStore<GridTopology | null>(newState.gridTopology),
        isLoading: false,
        initialLoadComplete: true,
      };
    }

    case 'UPDATE_NODE_POSITION': {
      if (!state.gridTopology) return state;
      
      const { nodeId, x, y } = action.payload;
      const newTopology: GridTopology = {
        ...state.gridTopology,
        nodes: state.gridTopology.nodes.map(node =>
          node.id === nodeId ? { ...node, x, y } : node
        ),
      };
      // Transient: a drag streams many updates; committed once on drag end.
      return editTopology(state, newTopology);
    }

    case 'UPDATE_NODE_COST': {
      if (!state.gridTopology) return state;
      
      const { nodeId, cost } = action.payload;
      const newTopology: GridTopology = {
        ...state.gridTopology,
        nodes: state.gridTopology.nodes.map(node =>
          node.id === nodeId ? { ...node, costPerMWh: cost } : node
        ),
      };
      // Transient: typing streams updates; committed once on input blur.
      return editTopology(state, newTopology);
    }

    case 'UPDATE_NODE_FUEL_TYPE': {
      if (!state.gridTopology) return state;
      
      const { nodeId, fuelType } = action.payload;
      const newTopology: GridTopology = {
        ...state.gridTopology,
        nodes: state.gridTopology.nodes.map(node =>
          node.id === nodeId ? { ...node, fuelType } : node
        ),
      };
      return commitTopology(state, newTopology);
    }

    case 'UPDATE_BATTERY_PARAMETERS': {
      if (!state.gridTopology) return state;
      
      const { batteryId, params } = action.payload;
      const newTopology: GridTopology = {
        ...state.gridTopology,
        nodes: state.gridTopology.nodes.map(node =>
          node.id === batteryId ? { ...node, ...params } : node
        ),
      };
      // Transient: typing streams updates; committed once on input blur.
      return editTopology(state, newTopology);
    }

    case 'UPDATE_EDGE_RATING': {
      if (!state.gridTopology) return state;
      
      const { edgeId, rating } = action.payload;
      const newTopology: GridTopology = {
        ...state.gridTopology,
        edges: state.gridTopology.edges.map(edge =>
          edge.id === edgeId ? { ...edge, rating } : edge
        ),
      };
      // Transient: dragging the slider streams updates; committed once on end.
      return editTopology(state, newTopology);
    }

    case 'ADD_NODE': {
      if (!state.gridTopology) return state;
      
      const { type, x, y } = action.payload;
      const nodeId = `${type}${state.nodeCounter}`;
      
      let newNode: GridNodeTopology = {
        id: nodeId,
        type,
        x,
        y,
      };

      // Set defaults based on type
      if (type === 'generator') {
        newNode = {
          ...newNode,
          minPowerMW: 0,
          maxPowerMW: 100,
          costPerMWh: 50,
          fuelType: 'fossil',
        };
      } else if (type === 'load') {
        newNode = {
          ...newNode,
          maxPowerMW: -50, // Default 50 MW load (negative for loads)
        };
      } else if (type === 'battery') {
        newNode = {
          ...newNode,
          maxChargeMW: 25,
          maxDischargeMW: 25,
          maxEnergyMWh: 100,
          minEnergyMWh: 0,
          efficiency: 0.9,
          initialEnergyMWh: 50,
        };
      }

      const newTopology: GridTopology = {
        ...state.gridTopology,
        nodes: [...state.gridTopology.nodes, newNode],
      };

      return {
        ...commitTopology(state, newTopology),
        nodeCounter: state.nodeCounter + 1,
      };
    }

    case 'REMOVE_NODE': {
      if (!state.gridTopology) return state;
      
      const nodeId = action.payload;
      const newTopology: GridTopology = {
        nodes: state.gridTopology.nodes.filter(node => node.id !== nodeId),
        edges: state.gridTopology.edges.filter(edge => edge.from !== nodeId && edge.to !== nodeId),
      };

      return commitTopology(state, newTopology);
    }

    case 'ADD_EDGE': {
      if (!state.gridTopology) return state;
      
      const { fromNodeId, toNodeId } = action.payload;
      const edgeId = `edge${state.edgeCounter}`;
      
      const newEdge: GridEdgeTopology = {
        id: edgeId,
        from: fromNodeId,
        to: toNodeId,
        resistance: 0.1, // Default value
        rating: 100, // Default rating in MW
      };

      const newTopology: GridTopology = {
        ...state.gridTopology,
        edges: [...state.gridTopology.edges, newEdge],
      };

      return {
        ...commitTopology(state, newTopology),
        edgeCounter: state.edgeCounter + 1,
      };
    }

    case 'REMOVE_EDGE': {
      if (!state.gridTopology) return state;
      
      const edgeId = action.payload;
      const newTopology: GridTopology = {
        ...state.gridTopology,
        edges: state.gridTopology.edges.filter(edge => edge.id !== edgeId),
      };

      return commitTopology(state, newTopology);
    }

    case 'REVEAL_IN_PANEL': {
      if (!state.gridTopology) return state;
      
      const nodeId = action.payload;
      const node = state.gridTopology.nodes.find(n => n.id === nodeId);
      if (!node) return state;

      // Determine which tab to show based on node type
      const targetTab: TabType =
        node.type === 'generator' ? 'gen' :
        node.type === 'load' ? 'loads' :
        node.type === 'battery' ? 'battery' : 'bus';

      // Bump revealSeq so the panel area opens/focuses the tab even when
      // targetTab already equals the current activeTab.
      return {
        ...state,
        activeTab: targetTab,
        revealSeq: state.revealSeq + 1,
      };
    }

    case 'HANDLE_LAYOUT_CHANGE': {
      return commitTopology(state, action.payload);
    }

    case 'IMPORT_GRID_TOPOLOGY': {
      const topology = action.payload;

      // Update counters based on imported data
      const maxNodeNum = Math.max(...topology.nodes.map((n: GridNodeTopology) => {
        const match = n.id.match(/\d+$/);
        return match ? parseInt(match[0]) : 0;
      }), 0);
      const maxEdgeNum = Math.max(...topology.edges.map((e: GridEdgeTopology) => {
        const match = e.id.match(/\d+$/);
        return match ? parseInt(match[0]) : 0;
      }), 0);

      return {
        ...loadDocument(state, topology, 'Imported'),
        nodeCounter: maxNodeNum + 1,
        edgeCounter: maxEdgeNum + 1,
      };
    }

    case 'NEW_SCENARIO': {
      const topology: GridTopology = { nodes: [], edges: [] };
      return {
        ...loadDocument(state, topology, 'Untitled'),
        nodeCounter: 1,
        edgeCounter: 1,
      };
    }

    case 'UNDO': {
      const history = state.history.undo();
      return { ...state, history, gridTopology: history.present };
    }

    case 'REDO': {
      const history = state.history.redo();
      return { ...state, history, gridTopology: history.present };
    }

    case 'COMMIT_TOPOLOGY': {
      // Record the current live topology as one undo step. No-op if nothing
      // changed since the last commit (e.g. a mouse-up with no drag, or a
      // blur with no edit), since transient edits always create a new object.
      if (state.gridTopology === state.history.present) return state;
      return { ...state, history: state.history.record(state.gridTopology) };
    }

    case 'UPDATE_LOAD_CURVE': {
      const newLoadCurve = {
        controlPoints: action.payload,
      };
      
      return {
        ...state,
        loadCurve: newLoadCurve,
      };
    }
    
    case 'UPDATE_WIND_CURVE': {
      const newWindCurve = {
        controlPoints: action.payload,
      };
      
      return {
        ...state,
        windCurve: newWindCurve,
      };
    }
    
    case 'UPDATE_SOLAR_CURVE': {
      const newSolarCurve = {
        controlPoints: action.payload,
      };
      
      return {
        ...state,
        solarCurve: newSolarCurve,
      };
    }
    
    case 'SET_HOVER_HOUR': {
      return {
        ...state,
        hoverState: {
          ...state.hoverState,
          hoverHour: action.payload,
        },
      };
    }
    
    case 'SET_HOVER_ELEMENT': {
      return {
        ...state,
        hoverState: {
          ...state.hoverState,
          element: action.payload,
        },
      };
    }
    
    case 'SET_HOVER_STATE': {
      return {
        ...state,
        hoverState: action.payload,
      };
    }

    case 'SET_SELECTED_ELEMENT': {
      return {
        ...state,
        selectedElement: action.payload,
      };
    }
    
    case 'SET_NUM_TIME_STEPS': {
      return {
        ...state,
        numTimeSteps: action.payload,
      };
    }

    case 'SET_MODE': {
      return {
        ...state,
        mode: action.payload,
      };
    }

    case 'SET_REQUIRE_MATCHING_SOC': {
      // The toggle *is* a rule edit: it picks between the two final-SOC
      // variants (end == start vs. end >= 90% of start), so it rewrites that
      // rule's text — overwriting any hand edits to it, which is what the
      // checkbox promises. A user who deleted the rule keeps it deleted.
      return {
        ...state,
        requireMatchingSOC: action.payload,
        rules: mapRule(state.rules, FINAL_SOC_RULE, (rule) =>
          renamed(rule, ruleToString(finalSocFor(action.payload))),
        ),
      };
    }

    case 'SET_RULE_SOURCE': {
      const { id, source } = action.payload;
      const key = rulesKey(state);
      return { ...state, [key]: mapRule(state[key], id, (rule) => renamed(rule, source)) };
    }

    case 'SET_RULE_ENABLED': {
      const { id, enabled } = action.payload;
      const key = rulesKey(state);
      return { ...state, [key]: mapRule(state[key], id, (rule) => ({ ...rule, enabled })) };
    }

    case 'ADD_RULE': {
      // The name lives in the rule's text, so adding one is just dropping in a
      // template that names itself — no prompt. The id is that starting name,
      // kept free of every name and id already in use.
      const key = rulesKey(state);
      const rs = state[key];
      const name = uniqueRuleName(rs.rules, NEW_RULE_NAME);
      const entry = { id: name, name, source: newRuleSource(name), enabled: true };
      return {
        ...state,
        [key]: { ...rs, rules: [...rs.rules, entry] },
        activeTab: `constraints:${entry.id}`,
        revealSeq: state.revealSeq + 1,
      };
    }

    case 'REMOVE_RULE': {
      const key = rulesKey(state);
      const rs = state[key];
      return { ...state, [key]: { ...rs, rules: rs.rules.filter((rule) => rule.id !== action.payload) } };
    }

    case 'SET_COST_SOURCE': {
      const { id, source } = action.payload;
      const key = rulesKey(state);
      return { ...state, [key]: mapCost(state[key], id, (cost) => renamed(cost, source, parseCostTerm)) };
    }

    case 'SET_COST_ENABLED': {
      const { id, enabled } = action.payload;
      const key = rulesKey(state);
      return { ...state, [key]: mapCost(state[key], id, (cost) => ({ ...cost, enabled })) };
    }

    case 'ADD_COST': {
      const key = rulesKey(state);
      const rs = state[key];
      const name = uniqueRuleName(rs.costs, NEW_COST_NAME);
      const entry = { id: name, name, source: newCostSource(name), enabled: true };
      return {
        ...state,
        [key]: { ...rs, costs: [...rs.costs, entry] },
        activeTab: `costs:${entry.id}`,
        revealSeq: state.revealSeq + 1,
      };
    }

    case 'REMOVE_COST': {
      const key = rulesKey(state);
      const rs = state[key];
      return { ...state, [key]: { ...rs, costs: rs.costs.filter((cost) => cost.id !== action.payload) } };
    }

    case 'RESET_RULES': {
      return state.mode === 'acpf'
        ? { ...state, acRules: defaultAcRuleSetSource() }
        : { ...state, rules: defaultRuleSetSource(state.requireMatchingSOC) };
    }

    default:
      return state;
  }
}